import React, { useRef, useState } from "react";

import { trimToDecimals, useTranslation } from "utils";

import { CropMixComposerGoalResponse, CropsOverviewTableColumn, CropsOverviewTableColumnConfig, CropsOverviewTableDataType, SortState } from "models";

import { Checkbox, Flex, Icon, OverflowEllipsisWithTooltip, Table, Tag, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider";
import { CMC_ROUTES } from "pages/app/crop-mix-composer/CropMixComposerOverlay";
import { ScoreCircleChart, ScoreColor } from "pages/app/crop-mix-composer/components/compositionStep/ScoreCircleChart";
import { TableColumnTitle } from "pages/app/crop-mix-composer/components/compositionStep/TableColumnTitle";
import { getCostSymbol, tableSorting } from "pages/app/crop-mix-composer/utils/cropMixComposer";

const LOW_MATCH_SCORE_THRESHOLD = 30;
const TOP_FIT_MATCH_SCORE_THRESHOLD = 75;

const CROP_COLUMN_WIDTH_PERCENT = 30;

const isLowMatchScoreDividerRow = (record: unknown) => Boolean((record as CropsOverviewTableDataType).isLowMatchScoreDivider);

export const CropsOverviewTable = ({
  cropMixComposerResponse,
  isPreparingPrint = false,
}: {
  cropMixComposerResponse?: CropMixComposerGoalResponse[];
  isPreparingPrint?: boolean;
}) => {
  const { t } = useTranslation("cropMixComposer");
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { aggregateGoalScores, aggregateMatchScore, currentStep, selectedGoals, weightShareDistribution, selectedCrops, setSelectedCrops, isChangingStep } =
    useCropMixComposerContext();
  const [sortState, setSortState] = useState<SortState>({
    column: CropsOverviewTableColumn.LOCATION_MATCH,
    direction: "desc",
  });

  const handleSort = (column: string) => {
    setSortState((prev) => ({
      column,
      direction: prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const onCheck = (checked: boolean, crop: CropMixComposerGoalResponse) => {
    if (isChangingStep !== null) return;

    setSelectedCrops?.((prev) => (checked ? [...prev, crop] : prev.filter((c) => c.crop.id !== crop.crop.id)));
  };

  const cropsOverviewTableColumnsConfigs: CropsOverviewTableColumnConfig[] = [
    {
      title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.COVER_CROP}`),
      dataIndex: CropsOverviewTableColumn.COVER_CROP,
      key: CropsOverviewTableColumn.COVER_CROP,
      className: "crop-name",
    },
    ...(currentStep === CMC_ROUTES.OVERVIEW
      ? [
          {
            title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.SEEDING_RATE}`),
            dataIndex: CropsOverviewTableColumn.SEEDING_RATE,
            key: CropsOverviewTableColumn.SEEDING_RATE,
            className: "seeding-rate",
          },
          {
            title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.WEIGHT_SHARE}`),
            dataIndex: CropsOverviewTableColumn.WEIGHT_SHARE,
            key: CropsOverviewTableColumn.WEIGHT_SHARE,
            className: "weight-share",
          },
        ]
      : []),
    {
      title: t(`page.${CMC_ROUTES.COMPOSITION}.tableHeader.${CropsOverviewTableColumn.LOCATION_MATCH}`),
      dataIndex: CropsOverviewTableColumn.LOCATION_MATCH,
      key: CropsOverviewTableColumn.LOCATION_MATCH,
      className: "location-match",
    },
    ...selectedGoals.map((goal, idx) => ({
      title: t(`goals.${goal}`),
      dataIndex: `goal${idx + 1}`,
      key: goal,
      className: `goal${idx + 1}`,
    })),
  ];

  const getColumns = (
    cropsOverviewTableColumnsConfigs: CropsOverviewTableColumnConfig[],
    sortState: SortState,
    handleSort: (key: string) => void
  ): CropsOverviewTableColumnConfig[] => {
    const otherColumnsCount = Math.max(cropsOverviewTableColumnsConfigs.length - 1, 1);
    const otherColumnWidth = (100 - CROP_COLUMN_WIDTH_PERCENT) / otherColumnsCount;
    const columnCount = cropsOverviewTableColumnsConfigs.length + 1;

    return [
      {
        title: " ",
        dataIndex: "select",
        key: "select",
        width: currentStep === CMC_ROUTES.COMPOSITION ? "3rem" : undefined,
        onCell: (record: unknown) => ({ colSpan: isLowMatchScoreDividerRow(record) ? columnCount : 1 }),
      },
      ...cropsOverviewTableColumnsConfigs.map((col) => ({
        title: (
          <TableColumnTitle
            title={col.title as string}
            columnKey={col.key}
            active={sortState.column === col.key}
            direction={sortState.direction}
            currentStep={currentStep}
            onClick={() => (currentStep === CMC_ROUTES.COMPOSITION ? handleSort(col.key) : null)}
            className={col.className}
          />
        ),
        dataIndex: col.dataIndex,
        key: col.key,
        width: col.key === CropsOverviewTableColumn.COVER_CROP ? `${CROP_COLUMN_WIDTH_PERCENT}%` : `${otherColumnWidth}%`,
        onCell: (record: unknown) => ({ colSpan: isLowMatchScoreDividerRow(record) ? 0 : 1 }),
      })),
    ];
  };

  const buildCropRow = (item: CropMixComposerGoalResponse, idx: number): CropsOverviewTableDataType => ({
    key: item.crop.id,
    select:
      currentStep === CMC_ROUTES.COMPOSITION ? (
        <Checkbox
          className="small-checkbox"
          checked={selectedCrops.some((crop: CropMixComposerGoalResponse) => crop.crop.id === item.crop.id)}
          onChange={(e) => {
            e.stopPropagation();
            onCheck?.(e.target.checked, item);
          }}
        />
      ) : null,
    coverCrop: (
      <Flex vertical>
        <Flex className="crop-name-container" align="center" gap="0.5rem">
          <OverflowEllipsisWithTooltip title={item.crop.name} className="crop-name">
            {item.crop.name}
          </OverflowEllipsisWithTooltip>
          {(item.matchScore || 0) >= TOP_FIT_MATCH_SCORE_THRESHOLD ? (
            <Tag className="top-fit-label paragraph-xs" colour="blue" text={t(`page.${CMC_ROUTES.COMPOSITION}.tableBody.topFit`)} />
          ) : null}
        </Flex>
        <OverflowEllipsisWithTooltip
          title={`${t(`plantFamilies.${item.crop.cropFamily}`)} · ${getCostSymbol(item.crop.coastCategory)}`}
          className="crop-details"
        >
          {t(`plantFamilies.${item.crop.cropFamily}`)} · {getCostSymbol(item.crop.coastCategory)}
        </OverflowEllipsisWithTooltip>
      </Flex>
    ),
    seedingRate: currentStep === CMC_ROUTES.OVERVIEW ? `${weightShareDistribution[item.crop.id].weightSeedingRate} kg/ha` : undefined,
    weightShare: currentStep === CMC_ROUTES.OVERVIEW ? `${trimToDecimals(weightShareDistribution[item.crop.id].weightShare, 1)}%` : undefined,
    locationMatch: (
      <ScoreCircleChart
        dataTestId={`table-row-${idx}-score-circle-chart-location-match`}
        score={item.matchScore || 0}
        color={ScoreColor.BROWN}
        size={32}
        disableAnimation={isPreparingPrint}
      />
    ),
    goal1: (
      <ScoreCircleChart
        dataTestId={`table-row-${idx}-score-circle-chart-1`}
        score={item.scores.find((score) => score.level2 === selectedGoals[0])?.score || 0}
        color={ScoreColor.PINK}
        size={32}
        disableAnimation={isPreparingPrint}
      />
    ),
    goal2: (
      <ScoreCircleChart
        dataTestId={`table-row-${idx}-score-circle-chart-2`}
        score={item.scores.find((score) => score.level2 === selectedGoals[1])?.score || 0}
        color={ScoreColor.LIGHT_BLUE}
        size={32}
        disableAnimation={isPreparingPrint}
      />
    ),
    goal3: (
      <ScoreCircleChart
        dataTestId={`table-row-${idx}-score-circle-chart-3`}
        score={item.scores.find((score) => score.level2 === selectedGoals[2])?.score || 0}
        color={ScoreColor.PURPLE}
        size={32}
        disableAnimation={isPreparingPrint}
      />
    ),
  });

  const lowMatchScoreDividerRow: CropsOverviewTableDataType = {
    key: "low-match-score-divider",
    isLowMatchScoreDivider: true,
    select: (
      <Flex className="low-match-score-info" align="center" gap="0.5rem">
        <Icon className="low-match-score-icon" iconName="tabler/bulb" size="m-light" />
        <Typography variant="paragraph-m" className="margin-0">
          {t(`page.${CMC_ROUTES.COMPOSITION}.lowMatchScoreInfo`)}
        </Typography>
      </Flex>
    ),
    coverCrop: null,
    locationMatch: null,
    goal1: null,
    goal2: null,
    goal3: null,
  };

  const sortedCrops = [...(cropMixComposerResponse || [])].sort((a, b) => tableSorting(a, b, sortState, t, selectedGoals));
  const shouldGroupLowMatchScoreCrops = currentStep === CMC_ROUTES.COMPOSITION;
  const recommendedCrops = shouldGroupLowMatchScoreCrops ? sortedCrops.filter((crop) => (crop.matchScore || 0) >= LOW_MATCH_SCORE_THRESHOLD) : sortedCrops;
  const lowMatchScoreCrops = shouldGroupLowMatchScoreCrops ? sortedCrops.filter((crop) => (crop.matchScore || 0) < LOW_MATCH_SCORE_THRESHOLD) : [];

  const tableData: CropsOverviewTableDataType[] = recommendedCrops.map(buildCropRow);

  if (lowMatchScoreCrops.length > 0) {
    tableData.push(lowMatchScoreDividerRow, ...lowMatchScoreCrops.map((crop, idx) => buildCropRow(crop, recommendedCrops.length + idx)));
  }

  const generateTotalRow = (selectedCrops: CropMixComposerGoalResponse[]): CropsOverviewTableDataType => {
    const totalSeedingRate = selectedCrops.reduce((sum, crop) => sum + (weightShareDistribution[crop.crop.id].weightSeedingRate || 0), 0);

    const weightedAggregateGoalScores = aggregateGoalScores.map((goal, idx) => (
      <ScoreCircleChart
        key={`total-goal-${idx}`}
        dataTestId={`total-score-circle-chart-${idx + 1}`}
        score={Math.round(goal.score || 0)}
        color={[ScoreColor.PINK, ScoreColor.LIGHT_BLUE, ScoreColor.PURPLE][idx]}
        size={32}
        disableAnimation={isPreparingPrint}
      />
    ));

    return {
      key: "total",
      select: null,
      coverCrop: (
        <Flex vertical>
          <Typography variant="paragraph-m" className="crop-name total-row">
            {t(`page.${CMC_ROUTES.COMPOSITION}.tableBody.total`)}
          </Typography>
        </Flex>
      ),
      seedingRate: `${trimToDecimals(totalSeedingRate)} kg/ha`,
      weightShare: "100%",
      locationMatch: (
        <ScoreCircleChart
          dataTestId="total-score-circle-chart-location-match"
          score={aggregateMatchScore || 0}
          color={ScoreColor.BROWN}
          size={32}
          disableAnimation={isPreparingPrint}
        />
      ),
      goal1: weightedAggregateGoalScores[0],
      goal2: weightedAggregateGoalScores[1],
      goal3: weightedAggregateGoalScores[2],
    };
  };

  if (currentStep === CMC_ROUTES.OVERVIEW && cropMixComposerResponse?.length) {
    const selectedCrops = cropMixComposerResponse.filter((crop) => tableData.some((row) => row.key === crop.crop.id));
    tableData.push(generateTotalRow(selectedCrops));
  }

  return (
    <div className={`crop-mix-composer-overlay-step-overview step${currentStep} table-container`} ref={tableContainerRef}>
      <Table
        className="crop-mix-composer-overlay-step-overview table"
        data-testid="crop-mix-composer-overlay-step-overview-table"
        columns={getColumns(cropsOverviewTableColumnsConfigs, sortState, handleSort)}
        data={tableData}
        pagination={false}
        setEqualWidthColumns={false}
        // Ant renders a sticky header as a second table, sized in pixels measured from the on-screen
        // layout, which no print stylesheet can realign against the page width.
        sticky={isPreparingPrint ? undefined : { getContainer: () => tableContainerRef.current || window }}
        rowClassName={(record) => (isLowMatchScoreDividerRow(record) ? "low-match-score-divider-row" : "")}
      />
    </div>
  );
};
